// ============================================================
// src/hooks/useNotifications.ts
// ============================================================
import { useState, useEffect, useCallback, useMemo } from 'react';
import { db, auth } from '@/lib/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import {
  UserNotification,
  NotificationType,
  NOTIFICATION_URGENCY_COLOR,
} from '@/types/notifications.types';

const STAFF_ROLES = ['staff', 'admin'];

const STAFF_ONLY_TYPES: NotificationType[] = [
  'deal_submitted',
  'cim_generated',
  'document_uploaded',
  'deal_event',
  'system',
  'message_unread_24h',
  'message_unread_48h',
  'deal_unreviewed_48h',
  'deal_unreviewed_72h',
];

export function useNotifications() {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (firebaseUser) => {
      if (!firebaseUser) { setUserRoles([]); return; }
      try {
        const tokenResult = await firebaseUser.getIdTokenResult(true);
        const roles = (tokenResult.claims.roles as string[]) || [];
        setUserRoles(roles);
      } catch {
        try {
          const { getDoc } = await import('firebase/firestore');
          const userSnap = await getDoc(doc(db, 'users', firebaseUser.uid));
          setUserRoles(userSnap.data()?.roles || []);
        } catch {
          setUserRoles([]);
        }
      }
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) { setLoading(false); return; }

    const isStaffOrAdmin = userRoles.some(r => STAFF_ROLES.includes(r));

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', firebaseUser.uid),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        let notifs = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || new Date(),
          seenAt: d.data().seenAt?.toDate?.() || undefined,
          archivedAt: d.data().archivedAt?.toDate?.() || undefined,
        })) as UserNotification[];

        // Exclure archivées
        notifs = notifs.filter(n => n.status !== 'archived');

        // Filtrer staff-only pour non-staff
        if (!isStaffOrAdmin) {
          notifs = notifs.filter(n => !STAFF_ONLY_TYPES.includes(n.type));
        }

        setNotifications(notifs);
        setLoading(false);
      },
      (err) => {
        console.error('[useNotifications] error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userRoles]);

  const markAsSeen = useCallback(async (notificationId: string) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        seen: true,
        status: 'read',
        seenAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('[useNotifications] markAsSeen error:', err);
    }
  }, []);

  const markAllAsSeen = useCallback(async () => {
    const unseen = notifications.filter(n => !n.seen);
    if (!unseen.length) return;
    try {
      const batch = writeBatch(db);
      unseen.forEach(n => {
        batch.update(doc(db, 'notifications', n.id), {
          seen: true,
          status: 'read',
          seenAt: Timestamp.now(),
        });
      });
      await batch.commit();
    } catch (err) {
      console.error('[useNotifications] markAllAsSeen error:', err);
    }
  }, [notifications]);

  const archiveNotification = useCallback(async (notificationId: string) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        status: 'archived',
        archivedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('[useNotifications] archive error:', err);
    }
  }, []);

  const archiveAllRead = useCallback(async () => {
    const read = notifications.filter(n => n.seen);
    if (!read.length) return;
    try {
      const batch = writeBatch(db);
      read.forEach(n => {
        batch.update(doc(db, 'notifications', n.id), {
          status: 'archived',
          archivedAt: Timestamp.now(),
        });
      });
      await batch.commit();
    } catch (err) {
      console.error('[useNotifications] archiveAllRead error:', err);
    }
  }, [notifications]);

  const getUrgencyColor = useCallback((type: NotificationType): string => {
    return NOTIFICATION_URGENCY_COLOR[type] || '#6B7280';
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter(n => !n.seen).length,
    [notifications]
  );

  const urgentCount = useMemo(
    () => notifications.filter(n => !n.seen && n.priority === 'urgent').length,
    [notifications]
  );

  const unreadNotifications = useMemo(
    () => notifications.filter(n => !n.seen),
    [notifications]
  );

  const readNotifications = useMemo(
    () => notifications.filter(n => n.seen),
    [notifications]
  );

  return {
    notifications,
    unreadNotifications,
    readNotifications,
    loading,
    error,
    unreadCount,
    urgentCount,
    markAsSeen,
    markAllAsSeen,
    archiveNotification,
    archiveAllRead,
    getUrgencyColor,
  };
}